import { BookingContext } from 'contexts';
import { useCallback, useState } from 'react';
import { uniqueId } from 'lodash';
import { toDateString, yearsSince } from '@koob/dates';
import { HOTELS_CATALOG_SCROLL_KEY } from 'constants';
import {
  TravelerKindEnum,
  PER_PAGE_FOR_HOTELS_AVAILABILITY,
} from '@koob/enums';
import {
  blankToUndefined,
  NATIONALITY_OPTIONS,
  splitFolderTravelers,
} from '@koob/utils';
import { useActiveFolder } from 'store';

export const getTravelerComposition = ({
  compositions,
  customerFile,
  typeFieldName = 'type',
  replaceBookingId = null,
  checkInDate,
}) => {
  const referenceDate = checkInDate ? new Date(checkInDate) : new Date();
  const travelerRooms = (compositions || []).reduce((acc, curr) => {
    const peoples = [];
    for (let i = 0; i < curr.adults; i++) {
      const id = uniqueId();
      const people = { id };
      people[typeFieldName] = TravelerKindEnum.ADULT;
      peoples.push(people);
    }
    for (let j = 0; j < curr.children; j++) {
      const id = uniqueId();
      const people = { id };
      people[typeFieldName] = TravelerKindEnum.CHILD;
      peoples.push(people);
    }
    acc.push({ ...curr, travelers: peoples });
    return acc;
  }, []);

  const formatTravelers = (rooms, people) => {
    const adults = people.filter(
      traveler =>
        !!traveler.birthdate &&
        yearsSince(traveler.birthdate, referenceDate) >= 18,
    );
    const childs = people.filter(
      traveler =>
        !!traveler.birthdate &&
        yearsSince(traveler.birthdate, referenceDate) < 18,
    );

    let adultIndex = 0;
    let childIndex = 0;

    rooms.forEach(room => {
      room.travelers.forEach((traveler, index) => {
        let person = undefined;

        if (traveler[typeFieldName] === TravelerKindEnum.ADULT) {
          if (adultIndex < adults.length) {
            person = adults[adultIndex];
            adultIndex++;
          }
        } else {
          if (childIndex < childs.length) {
            person = childs[childIndex];
            childIndex++;
          }
        }

        if (person) {
          // Same guard as the customerFile branch: a booking replayed from a
          // folder of placeholders carries nulls the pax form's Yup schemas
          // reject on sight.
          if (person.id) {
            traveler.id = person.id;
          }
          traveler.gender = blankToUndefined(person.gender);
          traveler.firstname = blankToUndefined(person.firstName);
          traveler.lastname = blankToUndefined(person.lastName);
          traveler.birthdate = blankToUndefined(person.birthdate);
          traveler.passportNumber = blankToUndefined(person.passportNumber);
          traveler.expirationDate = blankToUndefined(
            person.passportExpirationDate,
          );
          traveler.ageIsExact = blankToUndefined(person?.ageIsExact);

          traveler.nationality = blankToUndefined(
            index === 0 && traveler[typeFieldName] === TravelerKindEnum.ADULT
              ? room?.firstAdultNationality?.value || person.nationality
              : person.nationality,
          );
        } else {
          traveler.nationality =
            index === 0 &&
            traveler[typeFieldName] === TravelerKindEnum.ADULT &&
            room?.firstAdultNationality?.value;
        }
      });
    });
  };

  if (replaceBookingId && customerFile?.bookings) {
    const bookingToUpdate = customerFile.bookings.find(
      booking => booking.id === replaceBookingId,
    );

    if (bookingToUpdate) {
      const travelers = [];

      (bookingToUpdate.roomsBooked || []).forEach(room => {
        if (room.travelers) {
          travelers.push(...room.travelers);
        }
      });

      (bookingToUpdate.experiencesBooked || []).forEach(experience => {
        if (experience.travelers) {
          travelers.push(...experience.travelers);
        }
      });

      const unmatchedFolderTravelers = [...(customerFile.travelers ?? [])];

      const normalizedTravelers = travelers.map(traveler => {
        const folderTravelerIndex = unmatchedFolderTravelers.findIndex(
          folderTraveler =>
            folderTraveler.firstName === traveler.firstName &&
            folderTraveler.lastName === traveler.lastName &&
            toDateString(folderTraveler.birthdate) ===
              toDateString(traveler.birthdate),
        );
        const folderTraveler =
          folderTravelerIndex === -1
            ? undefined
            : unmatchedFolderTravelers.splice(folderTravelerIndex, 1)[0];

        return {
          id: folderTraveler?.id,
          gender: traveler.gender,
          firstName: traveler.firstName,
          lastName: traveler.lastName,
          birthdate: traveler.birthdate,
          nationality: traveler.nationality,
          passportNumber: traveler.passportNumber,
          passportExpirationDate: traveler.expirationDate,
          ageIsExact: traveler?.ageIsExact,
        };
      });

      formatTravelers(travelerRooms, normalizedTravelers);
    }
  } else if (customerFile) {
    let adultIndex = 0;
    let childIndex = 0;
    // KD-3546: seats are matched on the folder's own kind/position, not on an
    // age recomputed from the birthdate — a placeholder has no birthdate yet and
    // would otherwise be skipped, leaving booking to create a duplicate folder
    // traveler instead of completing it.
    const { adults, children: childs } = splitFolderTravelers(
      customerFile.travelers,
      referenceDate,
      customerFile.mainTravelerId,
    );

    travelerRooms.forEach(room => {
      room.travelers.forEach((traveler, index) => {
        let person = undefined;
        if (traveler[typeFieldName] === TravelerKindEnum.ADULT) {
          if (adultIndex < adults.length) {
            person = adults[adultIndex];
            adultIndex++;
          }
        } else {
          if (childIndex < childs.length) {
            person = childs[childIndex];
            childIndex++;
          }
        }
        if (person) {
          // A placeholder carries nulls, which the pax form's Yup schemas
          // reject on sight: an unfilled field stays undefined.
          traveler.id = person.id;
          traveler.gender = blankToUndefined(person.gender);
          traveler.firstname = blankToUndefined(person.firstName);
          traveler.lastname = blankToUndefined(person.lastName);
          traveler.birthdate = blankToUndefined(person.birthdate);
          traveler.nationality = blankToUndefined(
            index === 0 && traveler[typeFieldName] === TravelerKindEnum.ADULT
              ? room?.firstAdultNationality?.value || person.nationality
              : person.nationality,
          );
          traveler.passportNumber = blankToUndefined(person.passportNumber);
          traveler.expirationDate = blankToUndefined(
            person.passportExpirationDate,
          );
          traveler.ageIsExact = blankToUndefined(person?.ageIsExact);
        } else {
          traveler.nationality =
            index === 0 &&
            traveler[typeFieldName] === TravelerKindEnum.ADULT &&
            room?.firstAdultNationality?.value;
        }
      });
    });
  } else {
    travelerRooms.forEach(room => {
      room.travelers.forEach((traveler, index) => {
        if (index === 0 && traveler.type === TravelerKindEnum.ADULT) {
          traveler.nationality = room?.firstAdultNationality?.value;
        } else {
          traveler.nationality = null;
        }
      });
    });
  }
  return travelerRooms;
};

const BookingProvider = ({ children }) => {
  const [filters, setFilters] = useState({});
  const [currentSelectedRoom, setCurrentSelectedRoom] = useState();
  const [selectedRooms, setSelectedRooms] = useState([]);
  // The global ClientFolder store is the single source of truth for the active
  // folder (US-014). The booking context reads it, but still allows transient
  // per-booking overrides (edit/replace/inline-create). `undefined` override
  // means "follow the store"; any other value (folder or null) wins locally.
  const storeActiveFolder = useActiveFolder();
  const [customerFileOverride, setCustomerFileOverride] = useState(undefined);
  const customerFile =
    customerFileOverride === undefined
      ? (storeActiveFolder ?? undefined)
      : customerFileOverride;
  const setCustomerFile = setCustomerFileOverride;
  const [requestId, setRequestId] = useState();
  const [expirationDate, setExpirationDate] = useState();
  const [replaceBookingId, setReplaceBookingId] = useState(undefined);
  const [guests, setGuests] = useState([]);
  const [selectedSupplements, setSelectedSupplements] = useState([]);
  const [catalogPage, setCatalogPage] = useState(1);
  const [catalogPerPage, setCatalogPerPage] = useState(
    PER_PAGE_FOR_HOTELS_AVAILABILITY,
  );

  const resetBooking = useCallback(() => {
    setCurrentSelectedRoom(undefined);
    setSelectedRooms([]);
    setTravelerRooms([]);
    setSelectedSupplements([]);
    setExpirationDate(undefined);
  }, []);

  const resetContext = useCallback(() => {
    setFilters({});
    setCustomerFile(undefined);
    setCatalogPage(1);
    setCatalogPerPage(PER_PAGE_FOR_HOTELS_AVAILABILITY);
    sessionStorage.removeItem(HOTELS_CATALOG_SCROLL_KEY);
  }, []);

  const [travelerRooms, setTravelerRooms] = useState([]);

  const value = {
    filters,
    setFilters,
    resetBooking,
    currentSelectedRoom,
    setCurrentSelectedRoom,
    requestId,
    setRequestId,
    selectedRooms,
    setSelectedRooms: param => {
      setSelectedRooms(param);
      setTravelerRooms(
        getTravelerComposition({
          compositions: filters.rooms,
          customerFile,
          replaceBookingId,
        }),
      );
    },
    travelerRooms,
    customerFile,
    setCustomerFile,
    resetContext,
    expirationDate,
    setExpirationDate,
    nationalities: NATIONALITY_OPTIONS,
    replaceBookingId,
    setReplaceBookingId,
    guests,
    setGuests,
    selectedSupplements,
    setSelectedSupplements,
    catalogPage,
    setCatalogPage,
    catalogPerPage,
    setCatalogPerPage,
  };

  return (
    <BookingContext.Provider value={value}>{children}</BookingContext.Provider>
  );
};

export default BookingProvider;
